import api, { ApiResponse } from './api';

export interface Payroll {
  id: number;
  month: number;
  year: number;
  startDate?: string;
  endDate?: string;
  createdDate: string;
  monthName: string;
}

export interface SalaryDetail {
  salaryID: number;
  payrollID: number;
  employeeID: number;
  baseSalary?: number;
  allowances?: number;
  deductions?: number;
  netSalary?: number;
  firstName: string;
  lastName: string;
  employeeCode?: string;
  departmentName?: string;
  positionName?: string;
}

export interface PayrollDetail extends Payroll {
  salaries: SalaryDetail[];
  totalBaseSalary: number;
  totalAllowances: number;
  totalDeductions: number;
  totalNetSalary: number;
  employeeCount: number;
}

export interface PayrollCalculationResult {
  employeeId: number;
  employeeCode?: string;
  fullName: string;
  departmentName: string;
  positionName: string;
  workDays: number;
  overtimeHours: number;
  baseSalary: number;
  overtimePay: number;
  sgkEmployee: number;
  unemploymentEmployee: number;
  incomeTax: number;
  stampTax: number;
  totalDeductions: number;
  totalAllowances: number;
  netSalary: number;
}

export interface PayrollCalculation {
  year: number;
  month: number;
  monthName: string;
  startDate: string;
  endDate: string;
  employeeCount: number;
  totalBaseSalary: number;
  totalAllowances: number;
  totalDeductions: number;
  totalNetSalary: number;
  employees: PayrollCalculationResult[];
}

export interface PayrollStats {
  year: number;
  payrollCount: number;
  totalNetSalary: number;
  totalBaseSalary: number;
  totalAllowances: number;
  totalDeductions: number;
  monthlyTotals: {
    month: number;
    monthName: string;
    totalNet: number;
    totalBase: number;
    employeeCount: number;
  }[];
}

// ============================================================
// DETAYLI BORDRO MODELLERI (NM, EC, FM, HT, RT hesaplamalari)
// ============================================================

export interface PayrollRow {
  employeeId: number;
  sicilNo: string;
  adSoyad: string;
  bolum: string;

  // Calisma sureleri (gun)
  sgkGun: number;
  devamsizGun: number;
  ucretliIzinGun: number;
  ucretsizIzinGun: number;
  raporluGun: number;

  // Calisma sureleri (saat formatli string)
  normalMesaiStr: string;
  eksikCalismaStr: string;
  fazlaMesaiStr: string;
  haftaTatiliMesaiStr: string;
  resmiTatilMesaiStr: string;

  // Ucretler
  brutMaas: number;
  tamMesaiUcreti: number;
  normalMesaiUcreti: number;
  fazlaMesaiUcreti: number;
  haftaTatiliUcreti: number;
  resmiTatilUcreti: number;
  avansKesintisi: number;
  digerKesintiler: number;
  toplamBrut: number;
  toplamNet: number;

  // Formatli string
  tamMesaiUcretiStr: string;
  normalMesaiUcretiStr: string;
  fazlaMesaiUcretiStr: string;
  haftaTatiliUcretiStr: string;
  resmiTatilUcretiStr: string;
  toplamNetStr: string;
}

export interface PayrollSummary {
  toplamPersonel: number;
  toplamSgkGun: number;
  toplamDevamsizGun: number;
  toplamIzinGun: number;
  toplamBrutMaas: number;
  toplamNetMaas: number;
}

export interface PayrollReport {
  yil: number;
  ay: number;
  ayAdi: string;
  rows: PayrollRow[];
  summary: PayrollSummary;
  generatedAt: string;
  departmentId?: number;
  departmentName?: string;
}

export interface PayrollFilter {
  yil?: number;
  ay?: number;
  departmentId?: number;
  employeeId?: number;
  // 2026-08-15: Santiye / lokasyon filtresi (backend zaten destekliyordu)
  siteId?: number;
  locationId?: number;
  sadeceCalisanlar?: boolean;
}

export interface PayrollSettings {
  asgariUcret: number;
  fazlaMesaiKatsayi: number;
  haftaTatiliKatsayi: number;
  resmiTatilKatsayi: number;
  sgkIsciPayi: number;
  sgkIsverenPayi: number;
  issizlikIsciPayi: number;
  issizlikIsverenPayi: number;
  gelirVergisiOrani: number;
  damgaVergisiOrani: number;
  aylikCalismaSaati: number;
  gunlukCalismaSaati: number;
}

export interface SalaryCreateDto {
  employeeId: number;
  baseSalary: number;
  allowances: number;
  deductions: number;
  netSalary: number;
}

export interface PayrollCreateDto {
  year: number;
  month: number;
  salaries?: SalaryCreateDto[];
}

const payrollService = {
  getAll: async (year?: number, month?: number): Promise<ApiResponse<Payroll[]>> => {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());
    if (month) params.append('month', month.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return api.get(`/payroll${query}`);
  },

  getById: async (id: number): Promise<ApiResponse<PayrollDetail>> => {
    return api.get(`/payroll/${id}`);
  },

  // 2026-08-15 (kullanici talebi): employeeId verilirse SADECE o personel
  // icin bordro hesaplanir; verilmezse eskisi gibi tum aktif personel.
  calculate: async (
    year: number,
    month: number,
    employeeId?: number | null
  ): Promise<ApiResponse<PayrollCalculation>> => {
    return api.post('/payroll/calculate', {
      year,
      month,
      employeeId: employeeId && employeeId > 0 ? employeeId : undefined,
    });
  },

  create: async (data: PayrollCreateDto): Promise<ApiResponse<{ id: number }>> => {
    return api.post('/payroll', data);
  },

  delete: async (id: number): Promise<ApiResponse<{ id: number }>> => {
    return api.delete(`/payroll/${id}`);
  },

  getStats: async (year?: number): Promise<ApiResponse<PayrollStats>> => {
    const query = year ? `?year=${year}` : '';
    return api.get(`/payroll/stats${query}`);
  },

  // ============================================================
  // DETAYLI BORDRO ENDPOINT'LERI
  // ============================================================

  // Detayli bordro raporu - NM, EC, FM, HT, RT hesaplamalari ile
  getReport: async (filter: PayrollFilter): Promise<ApiResponse<PayrollReport>> => {
    const params = new URLSearchParams();
    if (filter.yil) params.append('year', filter.yil.toString());
    if (filter.ay) params.append('month', filter.ay.toString());
    if (filter.departmentId) params.append('departmentId', filter.departmentId.toString());
    if (filter.employeeId) params.append('employeeId', filter.employeeId.toString());
    if (filter.siteId) params.append('siteId', filter.siteId.toString());
    if (filter.locationId) params.append('locationId', filter.locationId.toString());
    if (filter.sadeceCalisanlar !== undefined) params.append('sadeceCalisanlar', filter.sadeceCalisanlar.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return api.get(`/payroll/report${query}`);
  },

  // Tek personelin detayli bordrosu
  getEmployeeReport: async (employeeId: number, year?: number, month?: number): Promise<ApiResponse<any>> => {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());
    if (month) params.append('month', month.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return api.get(`/payroll/report/employee/${employeeId}${query}`);
  },

  // Excel export
  exportToExcel: async (filter: PayrollFilter): Promise<void> => {
    const params = new URLSearchParams();
    if (filter.yil) params.append('year', filter.yil.toString());
    if (filter.ay) params.append('month', filter.ay.toString());
    if (filter.departmentId) params.append('departmentId', filter.departmentId.toString());
    if (filter.employeeId) params.append('employeeId', filter.employeeId.toString());
    if (filter.siteId) params.append('siteId', filter.siteId.toString());
    if (filter.locationId) params.append('locationId', filter.locationId.toString());
    if (filter.sadeceCalisanlar !== undefined) params.append('sadeceCalisanlar', filter.sadeceCalisanlar.toString());
    const query = params.toString() ? `?${params.toString()}` : '';

    // Dosya indirme icin
    const response = await fetch(`/api/payroll/report/export/excel${query}`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) throw new Error('Excel export hatasi');

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bordro_${filter.yil || new Date().getFullYear()}_${filter.ay || new Date().getMonth() + 1}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  },

  // Bordro ayarlari
  getSettings: async (): Promise<ApiResponse<PayrollSettings>> => {
    return api.get('/payroll/settings');
  },

  saveSettings: async (settings: PayrollSettings): Promise<ApiResponse<void>> => {
    return api.post('/payroll/settings', settings);
  },
};

export default payrollService;
